import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { user, contract } from "../stores/index";
import { navigate } from "svelte-routing";
import { globalLogin } from "./IDP";

export function createOidcService(idpConfig) {
  let userManager;

  function getManager() {
    if (!userManager) {
      userManager = new UserManager({
        authority: idpConfig.issuer,
        client_id: idpConfig.clientId,
        redirect_uri: idpConfig.redirectUri,
        post_logout_redirect_uri: idpConfig.redirectUri,
        response_type: "code",
        scope: idpConfig.scope ?? "openid email profile offline_access",
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        automaticSilentRenew: true,
        useRefreshTokens: true,
        ...(idpConfig.audience && {
          extraQueryParams: { audience: idpConfig.audience },
        }),
      });

      // Keep the Svelte store in sync whenever the library auto-renews the token
      userManager.events.addUserLoaded((token) => {
        setUserToken(token);
      });

      userManager.events.addSilentRenewError(async (e) => {
        console.log("Silent renew failed, logging out:", e);
        await logout();
      });

      // If the token has fully expired (silent renew didn't happen or failed), log out
      userManager.events.addAccessTokenExpired(async () => {
        await logout();
      });
    }
    return userManager;
  }

  function setUserToken(token) {
    const expirationDate = new Date(token.expires_at * 1000);

    let userStore;
    user.subscribe((data) => {
      userStore = data;
    });

    user.set({
      ...userStore,
      token: {
        token: token.access_token,
        type: token.token_type,
        exp: expirationDate,
      },
    });
  }

  // Only same-origin paths may be used as a post-login target, both to keep
  // history.replaceState() from throwing on a cross-origin URL and to close the
  // open redirect that an attacker-supplied ?returnTo= would otherwise allow.
  function safeReturnTo(value) {
    if (typeof value !== "string") return null;
    if (!value.startsWith("/") || value.startsWith("//")) return null;
    return value;
  }

  async function login(returnTo, loginHint) {
    try {
      const manager = getManager();
      // Full-page redirect rather than signinPopup(): WebKit (Safari, iOS)
      // drops the transient user activation across the await in resolveIDP(),
      // so window.open() returns null and oidc-client-ts fails with
      // "Attempted to navigate on a disposed window". returnTo travels in the
      // OIDC state because the IdP always calls back to the bare redirectUri.
      await manager.signinRedirect({
        prompt: "login",
        ...(loginHint && { login_hint: loginHint }),
        state: { returnTo },
      });
    } catch (e) {
      user.set(null);
      contract.set(null);
      throw e;
    }
  }

  async function getToken() {
    try {
      const manager = getManager();
      const token = await manager.signinSilent();
      if (!token) {
        return await logout();
      }
      setUserToken(token);
    } catch (e) {
      console.log("getToken error:", e);
      return await logout();
    }
  }

  async function logout() {
    try {
      const manager = getManager();
      user.set(null);
      contract.set(null);
      await manager.signoutRedirect();
    } catch (error) {
      console.log("logout error:", error);
      user.set(null);
      contract.set(null);
      navigate("/");
    }
  }

  // Runs in the main window when the IdP redirects back with ?code=/?error=.
  // Must complete before the router mounts, so the URL is rewritten to the
  // intended target first — globalLogin()'s navigate() cannot reach a Router
  // that does not exist yet.
  async function handleCallback() {
    const manager = getManager();
    try {
      const token = await manager.signinRedirectCallback();
      setUserToken(token);
      const returnTo = safeReturnTo(token.state?.returnTo);
      window.history.replaceState({}, "", returnTo ?? "/");
      await globalLogin(returnTo);
    } catch (e) {
      window.history.replaceState({}, "", "/");
      user.set(null);
      contract.set(null);
      throw e;
    }
  }

  function init() {
    getManager();
  }

  return { init, login, logout, getToken, handleCallback };
}
